import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { contactDetails, products } from "@/mocks/products";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahdvhroh653ivfvns90";

function KontaktPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value !== "string") return;
      if (value.trim() === "") return;
      params.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(
          String(serverMsg) ||
            "Ihre Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
        );
        setStatus("error");
      }
    } catch {
      setFormError(
        "Die Verbindung konnte nicht hergestellt werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.",
      );
      setStatus("error");
    }
  };

  const inputClass =
    "w-full h-11 px-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400";

  return (
    <SiteLayout>
      <div className="w-full bg-background-100 border-b border-background-200">
        <nav aria-label="Brotkrumen" className="w-full px-4 md:px-8 py-4">
          <ol className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">
                Start
              </Link>
            </li>
            <li aria-hidden="true">
              <i className="ri-arrow-right-s-line" />
            </li>
            <li className="text-foreground-800">Kontakt</li>
          </ol>
        </nav>
      </div>

      <section className="w-full bg-foreground-950 text-background-50">
        <div className="w-full px-4 md:px-8 py-14 md:py-16 max-w-3xl">
          <p className="font-label text-[11px] uppercase tracking-[0.18em] text-accent-300">
            Kontakt
          </p>
          <h1 className="mt-4 font-heading font-semibold text-3xl md:text-5xl leading-[1.1]">
            Sprechen Sie mit uns über Ihre Drückerverbindung
          </h1>
          <p className="mt-6 text-base md:text-lg text-background-200 leading-relaxed">
            Ob Standardteil oder Sonderausführung: Schildern Sie uns kurz Ihr Anliegen. Wir melden
            uns mit einer Rückmeldung oder einem konkreten Angebot.
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-14 items-start">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">
              Anfrageformular
            </h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              Felder mit Sternchen sind erforderlich. Für Sonderausführungen senden Sie uns bitte
              Muster oder Zeichnung per Post zu.
            </p>

            {status === "success" && (
              <div className="mt-6 flex items-start gap-3 rounded-md bg-secondary-100 border border-secondary-200 p-4">
                <i className="ri-checkbox-circle-line text-xl text-secondary-700 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="font-heading text-sm text-secondary-900">
                    Vielen Dank für Ihre Anfrage!
                  </p>
                  <p className="mt-1 text-sm text-secondary-900/80">
                    Wir haben Ihre Nachricht erhalten und melden uns zeitnah bei Ihnen.
                  </p>
                </div>
              </div>
            )}

            {status === "error" && (
              <div className="mt-6 flex items-start gap-3 rounded-md bg-primary-100 border border-primary-200 p-4">
                <i className="ri-error-warning-line text-xl text-primary-700 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-primary-900">{formError}</p>
              </div>
            )}

            <form
              id="kontaktformular"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div className="sm:col-span-1">
                <label htmlFor="firma" className="block font-label text-xs uppercase tracking-wide text-foreground-600 mb-1.5">
                  Firma
                </label>
                <input id="firma" name="firma" type="text" required className={inputClass} placeholder="Ihre Firma" />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="name" className="block font-label text-xs uppercase tracking-wide text-foreground-600 mb-1.5">
                  Name *
                </label>
                <input id="name" name="name" type="text" required className={inputClass} placeholder="Vor- und Nachname" />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="email" className="block font-label text-xs uppercase tracking-wide text-foreground-600 mb-1.5">
                  E-Mail *
                </label>
                <input id="email" name="email" type="email" required className={inputClass} placeholder="name@firma.de" />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="telefon" className="block font-label text-xs uppercase tracking-wide text-foreground-600 mb-1.5">
                  Telefon
                </label>
                <input id="telefon" name="telefon" type="tel" className={inputClass} placeholder="Für Rückfragen" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="interesse" className="block font-label text-xs uppercase tracking-wide text-foreground-600 mb-1.5">
                  Interesse an
                </label>
                <select id="interesse" name="interesse" defaultValue="" className={inputClass}>
                  <option value="" disabled>
                    Bitte wählen
                  </option>
                  {products.map((product) => (
                    <option key={product.slug} value={product.name}>
                      {product.name}
                    </option>
                  ))}
                  <option value="Sonderausführung">Sonderausführung / Sonstiges</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="nachricht" className="block font-label text-xs uppercase tracking-wide text-foreground-600 mb-1.5">
                  Ihre Nachricht *
                </label>
                <textarea
                  id="nachricht"
                  name="nachricht"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  placeholder="Beschreiben Sie kurz Ihr Anliegen, gewünschte Abmessungen oder Stückzahlen (max. 500 Zeichen)."
                />
              </div>

              <div className="honeypot-field" aria-hidden="true">
                <label htmlFor="website_alt">Website</label>
                <input
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-primary-600 text-background-50 font-label text-sm uppercase tracking-wide whitespace-nowrap hover:bg-primary-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="ri-send-plane-line text-base" aria-hidden="true" />
                  {status === "submitting" ? "Wird gesendet …" : "Anfrage absenden"}
                </button>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  Ihre Daten werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet.
                </p>
              </div>
            </form>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg bg-background-100 border border-background-200 p-6">
              <h2 className="font-heading font-semibold text-lg text-foreground-950">
                Kontaktdaten
              </h2>
              <ul className="mt-5 flex flex-col gap-4 text-sm text-foreground-700">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                    <i className="ri-building-2-line" aria-hidden="true" />
                  </span>
                  <span>
                    <strong className="block text-foreground-950">{contactDetails.company}</strong>
                    {contactDetails.location}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line" aria-hidden="true" />
                  </span>
                  <span>
                    <strong className="block text-foreground-950">Telefon</strong>
                    <a href={contactDetails.phoneHref} className="hover:text-primary-600 transition-colors">
                      {contactDetails.phone}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                    <i className="ri-printer-line" aria-hidden="true" />
                  </span>
                  <span>
                    <strong className="block text-foreground-950">Telefax</strong>
                    {contactDetails.fax}
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg bg-foreground-950 text-background-50 p-6">
              <h2 className="font-heading font-semibold text-lg">Sonderausführungen</h2>
              <p className="mt-3 text-sm text-background-200 leading-relaxed">
                Auf Anfrage fertigen wir für Sie Sonderausführungen an – Fertigungslose ab 300
                Stück. Senden Sie uns dazu idealerweise ein Muster oder eine Zeichnung.
              </p>
              <Link
                to="/unternehmen"
                className="mt-5 inline-flex items-center gap-2 font-label text-xs uppercase tracking-wide text-accent-300 hover:text-accent-200 transition-colors"
              >
                Mehr über uns
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}

export default KontaktPage;